import classNames from 'classnames';
import moment from 'moment';
import React, { useMemo, useState } from 'react';

import { Button, Callout, TimeFromNow } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { DoctorCard } from '../DoctorCard';
import { PleaseWait } from '../PleaseWait';
import { TelehealthAvailableModel, TelehealthAvailableRequest, telehealthService } from 'src/services/telehealth';

type Props = {
    className?: string;
    query: TelehealthAvailableRequest;
    onClick: (timeSlot: TelehealthAvailableModel) => void | Promise<void>;
    usePriorityWarning?: boolean;
};

export function SearchResults({ className, query, onClick, usePriorityWarning }: Props) {
    const [clicked, setClicked] = useState(false);

    const { data, error } = useAsyncData(
        {
            service: telehealthService,
            method: telehealthService.GetTelehealthAvailability,
        },
        query
    );

    const indexOfPriorityZero = useMemo(() => {
        return data?.findIndex((timeSlot) => timeSlot.priority === 0);
    }, [data]);

    if (!data) {
        return <PleaseWait hideText className={className} error={error} />;
    }

    if (!data.length) {
        return (
            <Callout color="error" className={className}>
                <strong>No GPs available.</strong> We’re sorry, but there are no Telehealth GPs available at the moment.
                Please try again in a few minutes, or visit one of our clinic’s online booking pages to book an
                appointment.
            </Callout>
        );
    }

    return (
        <div className={classNames(className, 'space-y-4')}>
            {data.map((timeSlot, index) => {
                const mom = moment(`${timeSlot.slot}`);

                return (
                    <React.Fragment key={timeSlot.id}>
                        {usePriorityWarning && index === indexOfPriorityZero ? (
                            <Callout color="warning">
                                No {index !== 0 && 'more'} results found. We couldn’t find any GPs available near your
                                location at the moment. However, here are some GPs that are available now.
                            </Callout>
                        ) : undefined}
                        <DoctorCard
                            doctor={timeSlot.user}
                            site={timeSlot.site}
                            gender={timeSlot.gender}
                            actions={
                                <>
                                    <div className="text-sm font-medium text-gray-500 sm:w-40">
                                        <p className="text-success-600 inline sm:block">Next available:</p>{' '}
                                        <p className="inline sm:block">
                                            <strong className="font-bold text-gray-900">{mom.format('LT')}</strong>{' '}
                                            <small className="text-xs">
                                                (<TimeFromNow time={mom.toDate()} />)
                                            </small>
                                        </p>
                                    </div>
                                    <Button
                                        className="mx-auto"
                                        disabled={clicked}
                                        onClick={async () => {
                                            setClicked(true);
                                            try {
                                                await onClick(timeSlot);
                                            } finally {
                                                setClicked(false);
                                            }
                                        }}
                                    >
                                        Book now
                                    </Button>
                                </>
                            }
                        />
                    </React.Fragment>
                );
            })}
        </div>
    );
}
